import { useState } from "react";
import { Plus, Pencil, Trash2, Loader2, Save, X, Dumbbell } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  code: "",
  title: "",
  description: "",
  video_url: "",
  image_url: "",
  common_mistakes: "",
  difficulty: "medium",
  equipment_name: "",
  equipment_url: "",
};

const DIFFICULTY_LABELS = { easy: "Facile", medium: "Medio", hard: "Difficile" };

export const ExerciseLibrary = ({ exercises, onRefresh }) => {
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  const openNew = () => {
    setForm(EMPTY);
    setEditing(null);
    setShowForm(true);
  };

  const openEdit = (ex) => {
    setForm({ ...EMPTY, ...ex });
    setEditing(ex.id);
    setShowForm(true);
  };

  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form };
      delete payload.id;
      delete payload.created_at;
      if (editing) {
        await axios.put(`${API}/exercises/${editing}`, payload, { withCredentials: true });
        toast.success("Esercizio aggiornato");
      } else {
        await axios.post(`${API}/exercises`, payload, { withCredentials: true });
        toast.success("Esercizio creato");
      }
      setShowForm(false);
      onRefresh();
    } catch (err) {
      toast.error("Errore nel salvataggio");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminare questo esercizio?")) return;
    try {
      await axios.delete(`${API}/exercises/${id}`, { withCredentials: true });
      toast.success("Esercizio eliminato");
      onRefresh();
    } catch (err) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const inputClass =
    "w-full px-4 py-3 rounded-xl border border-stone-200 focus:outline-none focus:ring-2 focus:ring-[#2c4c3b]/20 text-sm";

  return (
    <div data-testid="exercise-library">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-serif text-xl text-stone-900">Libreria Esercizi ({exercises.length})</h2>
        <Button onClick={openNew} className="bg-[#2c4c3b] hover:bg-[#1a2e22] text-stone-50 rounded-full" data-testid="new-exercise-btn">
          <Plus className="w-4 h-4 mr-2" />
          Nuovo Esercizio
        </Button>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-6 sm:p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-serif text-2xl text-stone-900">{editing ? "Modifica Esercizio" : "Nuovo Esercizio"}</h3>
              <button onClick={() => setShowForm(false)} className="p-2 hover:bg-stone-100 rounded-full">
                <X className="w-5 h-5 text-stone-500" />
              </button>
            </div>
            <form onSubmit={handleSave} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-stone-600 mb-2">Codice *</label>
                  <input type="text" value={form.code} onChange={set("code")} required placeholder="PH-CERV-001" className={inputClass} data-testid="exercise-code-input" />
                </div>
                <div>
                  <label className="block text-sm text-stone-600 mb-2">Difficoltà *</label>
                  <select value={form.difficulty} onChange={set("difficulty")} className={inputClass} data-testid="exercise-difficulty-select">
                    {Object.entries(DIFFICULTY_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm text-stone-600 mb-2">Titolo *</label>
                <input type="text" value={form.title} onChange={set("title")} required placeholder="Retrazione cervicale" className={inputClass} data-testid="exercise-title-input" />
              </div>
              <div>
                <label className="block text-sm text-stone-600 mb-2">Descrizione *</label>
                <textarea value={form.description} onChange={set("description")} required rows={3} className={`${inputClass} resize-none`} data-testid="exercise-description-input" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-stone-600 mb-2">Video (URL YouTube/Vimeo/mp4)</label>
                  <input type="url" value={form.video_url || ""} onChange={set("video_url")} placeholder="https://youtube.com/watch?v=..." className={inputClass} data-testid="exercise-video-input" />
                </div>
                <div>
                  <label className="block text-sm text-stone-600 mb-2">Immagine (URL)</label>
                  <input type="url" value={form.image_url || ""} onChange={set("image_url")} placeholder="https://..." className={inputClass} data-testid="exercise-image-input" />
                </div>
              </div>
              <div>
                <label className="block text-sm text-stone-600 mb-2">Errori da evitare</label>
                <textarea value={form.common_mistakes || ""} onChange={set("common_mistakes")} rows={2} placeholder="Non inarcare la schiena..." className={`${inputClass} resize-none`} data-testid="exercise-mistakes-input" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-stone-600 mb-2">Attrezzatura necessaria</label>
                  <input type="text" value={form.equipment_name || ""} onChange={set("equipment_name")} placeholder="Es: Elastico di resistenza" className={inputClass} data-testid="exercise-equipment-name-input" />
                </div>
                <div>
                  <label className="block text-sm text-stone-600 mb-2">Link acquisto attrezzo</label>
                  <input type="url" value={form.equipment_url || ""} onChange={set("equipment_url")} placeholder="https://... (shop/Amazon)" className={inputClass} data-testid="exercise-equipment-url-input" />
                </div>
              </div>
              <div className="flex gap-4 pt-2">
                <Button type="button" variant="outline" onClick={() => setShowForm(false)} className="flex-1 rounded-full py-6">
                  Annulla
                </Button>
                <Button type="submit" disabled={loading} className="flex-1 bg-[#2c4c3b] hover:bg-[#1a2e22] text-stone-50 rounded-full py-6" data-testid="save-exercise-btn">
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (<><Save className="w-4 h-4 mr-2" />Salva</>)}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {exercises.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-2xl border border-stone-100">
            <Dumbbell className="w-12 h-12 text-stone-200 mx-auto mb-4" />
            <p className="text-stone-500">Nessun esercizio nella libreria</p>
          </div>
        ) : (
          exercises.map((ex) => (
            <div key={ex.id} className="bg-white rounded-2xl p-5 border border-stone-100 flex items-start justify-between gap-4" data-testid={`exercise-row-${ex.code}`}>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-1.5">
                  <span className="text-[11px] font-mono px-2 py-0.5 rounded-full bg-stone-100 text-stone-500">{ex.code}</span>
                  <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#e8ece6] text-[#2c4c3b]">{DIFFICULTY_LABELS[ex.difficulty] || ex.difficulty}</span>
                </div>
                <h3 className="font-medium text-stone-900">{ex.title}</h3>
                <p className="text-sm text-stone-500 truncate">{ex.description}</p>
              </div>
              <div className="flex gap-1 flex-shrink-0">
                <button onClick={() => openEdit(ex)} className="p-2 text-stone-400 hover:text-[#2c4c3b] hover:bg-[#e8ece6] rounded-lg transition-colors" data-testid={`edit-exercise-${ex.code}`}>
                  <Pencil className="w-4 h-4" />
                </button>
                <button onClick={() => handleDelete(ex.id)} className="p-2 text-stone-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors" data-testid={`delete-exercise-${ex.code}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ExerciseLibrary;
